import { useState } from 'react';
import { Save, X } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { FieldError } from '../ui/FieldError';
import { inputClass } from '../../lib/validation';
import { disciplines } from '../../lib/disciplines';
import type { TrainingPlan, PlanFormData } from '../../types';

interface PlanFormProps {
  studentId: string;
  initialData?: TrainingPlan;
  onSubmit: (data: PlanFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function PlanForm({ studentId, initialData, onSubmit, onCancel, isLoading }: PlanFormProps) {
  const [formData, setFormData] = useState<PlanFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    discipline: initialData?.discipline ?? null,
    startDate: initialData?.startDate
      ? initialData.startDate.split('T')[0]
      : new Date().toISOString().split('T')[0],
    endDate: initialData?.endDate ? initialData.endDate.split('T')[0] : '',
    studentId: studentId,
    isActive: initialData?.isActive ?? true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.name.trim()) e.name = 'El nombre del plan es obligatorio';
    if (!formData.startDate) e.startDate = 'La fecha de inicio es obligatoria';
    if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) {
      e.endDate = 'La fecha de fin no puede ser anterior a la de inicio';
    }
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div>
        <label htmlFor="name" className="label">Nombre del Plan *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          aria-invalid={!!errors.name}
          className={inputClass(!!errors.name)}
          placeholder="Ej: Hipertrofia - Fase 1"
        />
        <FieldError message={errors.name} />
      </div>

      <div>
        <label htmlFor="discipline" className="label">Disciplina</label>
        <select
          id="discipline"
          name="discipline"
          value={formData.discipline ?? ''}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              discipline: (e.target.value || null) as PlanFormData['discipline'],
            }))
          }
          className="input"
        >
          <option value="">Sin especificar</option>
          {disciplines.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="description" className="label">Descripción</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="input resize-none"
          placeholder="Objetivos, consideraciones, etc."
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="startDate" className="label">Fecha de Inicio *</label>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            aria-invalid={!!errors.startDate}
            className={inputClass(!!errors.startDate)}
          />
          <FieldError message={errors.startDate} />
        </div>
        <div>
          <label htmlFor="endDate" className="label">Fecha de Fin (opcional)</label>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            aria-invalid={!!errors.endDate}
            className={inputClass(!!errors.endDate)}
          />
          <FieldError message={errors.endDate} />
        </div>
      </div>

      {initialData && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
          />
          <span className="text-dark-300 text-sm">Plan activo</span>
        </label>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
        <button type="button" onClick={onCancel} className="btn-secondary">
          <X className="w-4 h-4" />
          Cancelar
        </button>
        <button type="submit" disabled={isLoading} className="btn-primary">
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              {initialData ? 'Guardar Cambios' : 'Crear Plan'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

